import React, { useState } from "react";
import { Link } from "react-router-dom";
import CheckIcon from "../../../components/common/CheckIcon";
import { PaymentOptions } from "../../../components/common/PaymentOptions";
import { useInitializePaymentMutation } from "../../../app/api/dBCApi";

const DBCCheckout: React.FC = () => {
  // Pricing values
  const fullPrice = 1497300;
  const splitFirst = 999900;
  const splitSecond = 999900;

  const features = [
    "4-week intensive practical training",
    "3 months ongoing support (community + resources)",
    "Bi-weekly coaching calls (twice per month) for 3 months",
    "Live Q&A sessions every week during the 4-week program",
    "Private WhatsApp/Telegram community",
    "Downloadable worksheets and templates",
    "Lifetime access to core course materials",
    "Free updates and new content",
    "30-day money-back guarantee",
  ];

  // PAYMENT MUTATION
  const [initializePayment] = useInitializePaymentMutation();

  const [selectedPayment, setSelectedPayment] = useState<"full" | "split">(
    "full",
  );

  /**
   * Handles enrollment and initializes payment via Paystack
   */
  const handleEnrollmentSubmit = async (payload: {
    name: string;
    email: string;
    phone: string;
    paymentPlan: "full" | "split";
  }) => {
    try {
      const toBePaid = payload.paymentPlan === "full" ? fullPrice : splitFirst;

      const numericAmount = toBePaid / 100;

      const res = await initializePayment({
        email: payload.email,
        name: payload.name,
        amount: numericAmount,
        paymentPlan: payload.paymentPlan,
        metadata: {
          phone: payload.phone,
          course: "Digital Income Blueprint",
        },
      }).unwrap();

      if (res?.authorization_url) {
        window.location.href = res.authorization_url; // Redirect to Paystack
      } else {
        alert("Payment could not be initialized. Please try again.");
      }
    } catch (err: any) {
      console.error("Payment init error:", err);
      alert(
        err?.data?.message ||
          err?.message ||
          "Something went wrong while starting payment.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-white text-brandNavy">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-brandNavy via-brandLightNavy to-brandNavy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/assets/images/course-pattern.svg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-12 lg:py-12">
          <div className="text-center">
            <div className="inline-block bg-brandRed px-6 py-2 rounded-full text-sm font-bold mb-6">
              🎓 COMPLETE COURSE ACCESS
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              Digital Income Blueprint
            </h1>

            <p className="text-2xl md:text-3xl text-white/90 mb-4 font-semibold">
              Discover, Build, Convert
            </p>

            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
              The 3-Step System for Busy Nigerian Professionals to Build
              Profitable Online Businesses Without Quitting Their Day Job
            </p>

            <a
              href="#pricing"
              className="inline-block bg-brandRed text-white px-10 py-4 rounded-lg font-bold text-xl hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Enroll Now →
            </a>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-brandLightNavy/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Transform Your Expertise Into Income
            </h2>
            <p className="text-xl text-mutedGray max-w-3xl mx-auto">
              This practical, fast-paced program gives you a tested path from
              idea to first sales — plus the support to scale it.
            </p>
          </div>
        </div>
      </section>

      {/* Payment Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">
            Choose Your Payment Plan
          </h2>
          <p className="text-center text-mutedGray mb-8">
            Pick the plan that suits you — both include full program access and
            support.
          </p>

          <PaymentOptions
            fullPrice={fullPrice}
            splitFirst={splitFirst}
            splitSecond={splitSecond}
            features={features}
            initialSelected={selectedPayment}
            onSelect={(plan) => setSelectedPayment(plan)}
            onSubmit={handleEnrollmentSubmit}
            labels={{ full: "Full Payment", split: "Split Payment" }}
          />
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 bg-brandLightNavy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckIcon className="text-brandRed text-2xl" />
              </div>
              <div className="font-semibold text-brandNavy">
                30-Day Guarantee
              </div>
              <div className="text-sm text-mutedGray">Risk-free enrollment</div>
            </div>

            <div>
              <div className="w-16 h-16 bg-brandLightNavy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckIcon className="text-brandRed text-2xl" />
              </div>
              <div className="font-semibold text-brandNavy">
                Lifetime Access
              </div>
              <div className="text-sm text-mutedGray">Core materials</div>
            </div>

            <div>
              <div className="w-16 h-16 bg-brandLightNavy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckIcon className="text-brandRed text-2xl" />
              </div>
              <div className="font-semibold text-brandNavy">
                Bi-weekly Calls
              </div>
              <div className="text-sm text-mutedGray">
                Twice per month coaching
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brandNavy text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white/70 text-sm">
            © {new Date().getFullYear()} D.B.C. Formula. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="text-white/70 hover:text-white">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/70 hover:text-white">
              Terms of Service
            </Link>
            <Link to="/contact" className="text-white/70 hover:text-white">
              Contact Us
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DBCCheckout;
